import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';

import api from '../../../../services/api';

// Seção ADITIVA "Envios / Pedidos vinculados" (separação parcial do
// 4LogX Operation). Só aparece quando o pedido tem família vinculada por
// referenceCode — pedidos do fluxo atual sem parcial não mostram nada.

interface IShipmentItem {
  id: number;
  description: string;
  sku: string | null;
  quantity: number;
  scanDate: string | null;
  missingReason: string | null;
}

interface IShipmentOrder {
  id: number;
  orderCode: string;
  referenceCode: string | null;
  number: string;
  identification: string | null;
  statusCode: string;
  conferenceStatus: string;
  isPartialConferred: boolean;
  dateMarkAsDelivered: string | null;
  route: { id: number; name: string; status: string } | null;
  items: IShipmentItem[];
}

interface IProps {
  orderId: number;
}

const situationChip = (order: IShipmentOrder) => {
  if (order.dateMarkAsDelivered || order.statusCode === 'delivered')
    return { label: 'Entregue', color: '#48AE3D' };
  if (order.statusCode === 'on-route')
    return { label: 'Em rota', color: '#0E52A0' };
  if (order.conferenceStatus === 'conferred')
    return {
      label: order.isPartialConferred ? 'Envio parcial' : 'Separado',
      color: '#48AE3D',
    };
  if (order.conferenceStatus === 'partially-conferred')
    return { label: 'Parcial', color: '#B7791F' };
  return { label: 'Aguardando separação', color: '#718096' };
};

const formatDate = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString(
    'pt-BR',
    { hour: '2-digit', minute: '2-digit' },
  )}`;
};

export const OrderShipments = ({ orderId }: IProps) => {
  const [orders, setOrders] = useState<IShipmentOrder[]>([]);

  useEffect(() => {
    let active = true;
    api
      .get(`/operation/orders/${orderId}/shipments`)
      .then((response) => {
        if (active && response.data?.orders) setOrders(response.data.orders);
      })
      .catch(() => {
        // API antiga sem o módulo operation: seção simplesmente não aparece
      });
    return () => {
      active = false;
    };
  }, [orderId]);

  // fluxo atual (sem parcial): família de 1, nada é renderizado
  if (orders.length <= 1) return null;

  return (
    <Box margin={1} mb={2}>
      <Typography variant="body1" gutterBottom component="div">
        Envios / Pedidos vinculados:
      </Typography>
      <Table size="small" aria-label="envios">
        <TableHead>
          <TableRow>
            <TableCell>Pedido</TableCell>
            <TableCell>Situação</TableCell>
            <TableCell>Rota</TableCell>
            <TableCell>Entrega</TableCell>
            <TableCell>Itens</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((shipment) => {
            const chip = situationChip(shipment);
            const missing = shipment.items.filter((i) => i.missingReason);
            return (
              <TableRow key={shipment.id}>
                <TableCell>
                  {shipment.identification || shipment.number}
                  {shipment.id === orderId ? ' (este pedido)' : ''}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={chip.label}
                    style={{
                      backgroundColor: chip.color,
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>{shipment.route?.name || '-'}</TableCell>
                <TableCell>
                  {formatDate(shipment.dateMarkAsDelivered)}
                </TableCell>
                <TableCell>
                  {shipment.items.reduce((sum, i) => sum + i.quantity, 0)}{' '}
                  vol.
                  {missing.length > 0 && (
                    <Tooltip
                      title={missing
                        .map((i) => `${i.description}: ${i.missingReason}`)
                        .join(' · ')}
                    >
                      <span
                        style={{
                          color: '#B7791F',
                          fontWeight: 600,
                          marginLeft: 6,
                          cursor: 'help',
                        }}
                      >
                        ⚠ falta
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
